import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import SEO from "@/components/SEO";
import {
    LineChart, Line, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, AreaChart, Area,
} from "recharts";
import {
    ShoppingBag, Wallet, BookOpen, Users, ArrowUpRight, AlertTriangle, Trophy,
} from "lucide-react";

export default function AdminOverview() {
    const [stats, setStats] = useState(null);
    useEffect(() => { api.get("/admin/stats").then((r) => setStats(r.data)); }, []);
    if (!stats) return <div>Loading...</div>;

    const cards = [
        { label: "Total Revenue", value: `৳${stats.revenue.toLocaleString()}`, sub: "Confirmed/Delivered", icon: Wallet, color: "from-[#0B1E8A] to-[#08145A]" },
        { label: "Total Orders", value: stats.total_orders, sub: `${stats.pending_orders} pending`, icon: ShoppingBag, color: "from-[#08145A] to-[#0B1E8A]" },
        { label: "Total Books", value: stats.total_books, sub: `${stats.low_stock.length} low stock`, icon: BookOpen, color: "from-[#0B1E8A] to-[#1e40af]" },
        { label: "Subscribers", value: stats.total_subscribers, sub: "Newsletter", icon: Users, color: "from-[#08145A] to-[#1e40af]" },
    ];

    return (
        <div className="space-y-6" data-testid="admin-overview">
            <SEO title="Admin - Overview" />
            <div>
                <h1 className="text-2xl md:text-3xl font-bold text-brand-navy">Dashboard Overview</h1>
                <p className="text-brand-navy/55 text-sm mt-1">Realtime sales, orders & inventory snapshot</p>
            </div>

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4" data-testid="admin-stats-cards">
                {cards.map((c) => {
                    const Icon = c.icon;
                    return (
                        <div key={c.label} className={`relative overflow-hidden rounded-xl sm:rounded-2xl p-3 sm:p-5 text-white bg-gradient-to-br ${c.color} shadow-soft`}>
                            <Icon className="w-5 h-5 sm:w-7 sm:h-7 opacity-90" />
                            <div className="text-lg sm:text-2xl font-bold mt-2 sm:mt-3">{c.value}</div>
                            <div className="text-[11px] sm:text-xs text-white/70 mt-1">{c.label}</div>
                            <div className="text-[10px] text-white/60 mt-0.5">{c.sub}</div>
                            <ArrowUpRight className="absolute top-3 right-3 sm:top-4 sm:right-4 w-3.5 h-3.5 sm:w-4 sm:h-4 opacity-30" />
                        </div>
                    );
                })}
            </div>

            <div className="grid lg:grid-cols-3 gap-4 sm:gap-6">
                <div className="lg:col-span-2 bg-white rounded-xl sm:rounded-2xl p-4 sm:p-6 border border-brand-light shadow-soft" data-testid="admin-sales-chart">
                    <h3 className="font-bold text-brand-navy text-sm sm:text-base">Sales Last 7 Days</h3>
                    <p className="text-xs text-brand-navy/55">Revenue & order count trend</p>
                    <div className="mt-4 h-48 sm:h-64">
                        <ResponsiveContainer width="100%" height="100%">
                            <AreaChart data={stats.sales_last_7}>
                                <defs>
                                    <linearGradient id="grad1" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="0%" stopColor="#0B1E8A" stopOpacity={0.4} />
                                        <stop offset="100%" stopColor="#0B1E8A" stopOpacity={0} />
                                    </linearGradient>
                                </defs>
                                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                                <XAxis dataKey="date" tickFormatter={(d) => d.slice(5)} stroke="#94A3B8" fontSize={11} />
                                <YAxis stroke="#94A3B8" fontSize={11} />
                                <Tooltip />
                                <Area type="monotone" dataKey="revenue" stroke="#0B1E8A" strokeWidth={2} fill="url(#grad1)" name="Revenue (৳)" />
                            </AreaChart>
                        </ResponsiveContainer>
                    </div>
                </div>
                <div className="bg-white rounded-xl sm:rounded-2xl p-4 sm:p-6 border border-brand-light shadow-soft">
                    <h3 className="font-bold text-brand-navy text-sm sm:text-base">Orders Per Day</h3>
                    <p className="text-xs text-brand-navy/55">Last 7 days</p>
                    <div className="mt-4 h-48 sm:h-64">
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={stats.sales_last_7}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                                <XAxis dataKey="date" tickFormatter={(d) => d.slice(5)} stroke="#94A3B8" fontSize={11} />
                                <YAxis stroke="#94A3B8" fontSize={11} />
                                <Tooltip />
                                <Bar dataKey="orders" fill="#D61F1F" radius={[6, 6, 0, 0]} />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                </div>
            </div>

            <div className="grid lg:grid-cols-2 gap-4 sm:gap-6">
                <div className="bg-white rounded-xl sm:rounded-2xl p-4 sm:p-6 border border-brand-light shadow-soft">
                    <h3 className="font-bold text-brand-navy text-sm sm:text-base flex items-center gap-2"><Trophy className="w-4 h-4 text-brand-red shrink-0" /> Top Selling Books</h3>
                    {stats.top_books.length === 0 ? (
                        <p className="text-sm text-brand-navy/55 mt-3">No sales yet</p>
                    ) : (
                        <div className="mt-4 space-y-3">
                            {stats.top_books.map((b, i) => (
                                <div key={i} className="flex items-center gap-3">
                                    <img src={b.cover_image} alt={b.title} className="w-10 h-12 rounded-md object-cover" />
                                    <div className="flex-1 min-w-0">
                                        <div className="text-sm font-medium text-brand-navy truncate">{b.title}</div>
                                        <div className="text-xs text-brand-navy/55">{b.sold} sold</div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                <div className="bg-white rounded-xl sm:rounded-2xl p-4 sm:p-6 border border-brand-light shadow-soft">
                    <h3 className="font-bold text-brand-navy text-sm sm:text-base flex items-center gap-2"><AlertTriangle className="w-4 h-4 text-brand-red shrink-0" /> Low Stock Alerts</h3>
                    {stats.low_stock.length === 0 ? (
                        <p className="text-sm text-brand-navy/55 mt-3">All books well stocked ✨</p>
                    ) : (
                        <div className="mt-4 space-y-2">
                            {stats.low_stock.map((b) => (
                                <div key={b.id} className="flex items-center justify-between p-2 bg-brand-light/30 rounded-lg">
                                    <div className="text-sm font-medium text-brand-navy truncate">{b.title}</div>
                                    <div className="text-xs font-bold text-brand-red">{b.stock} left</div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
